import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ScrollText, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
  Mail, BellRing, Database, KeyRound, ShieldAlert, CircleDot,
} from 'lucide-react';
import { inputCls } from '../components/shared/FormKit';
import ResultCount from '../components/shared/ResultCount';
import { fetchJSON } from '../utils/api';

// Activity Log — the one place to see what the system did: every email it
// sent (or skipped), every weekly reminder run, every data change (settings
// and workflow-driven writes) and every sign-in / security event. Read-only
// over audit_logs via GET /api/admin/activity-log. Rendered as a panel inside
// the Admin Settings page (SettingsAdmin.js), which carries the page chrome
// and the admin:settings gate.

const PAGE_SIZE = 50;

const CATEGORY_TABS = [
  { id: '', name: 'All', icon: ScrollText },
  { id: 'mail', name: 'Emails', icon: Mail },
  { id: 'reminder', name: 'Reminder Runs', badge: 'Reminder runs', icon: BellRing },
  { id: 'data', name: 'Data Changes', badge: 'Data changes', icon: Database },
  { id: 'auth', name: 'Sign-in', icon: KeyRound },
  { id: 'security', name: 'Security', icon: ShieldAlert },
  { id: 'other', name: 'Other', icon: CircleDot },
];

const CATEGORY_BADGE = {
  mail: 'bg-blue-50 text-blue-700',
  reminder: 'bg-violet-50 text-violet-700',
  data: 'bg-emerald-50 text-emerald-700',
  auth: 'bg-slate-100 text-slate-700',
  security: 'bg-red-50 text-red-700',
  other: 'bg-slate-100 text-slate-500',
};

const THEAD_CLS = 'px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider';

// Timestamps are shown in Singapore time whatever the server clock is.
const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return String(iso); }
};

const todayYmd = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Top-level keys whose value differs between the before/after snapshots of a
// data_updated row — enough to say what changed without opening the JSON.
const changedKeys = (before, after) => {
  if (!before || !after || typeof before !== 'object' || typeof after !== 'object') return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]));
};

// One human line per row. Falls back to a trimmed JSON snippet.
const summarise = (row) => {
  const d = row.details || {};
  switch (row.action) {
    case 'email_sent': {
      const to = Array.isArray(d.recipients) ? d.recipients.join(', ') : (d.recipients || '');
      const flags = [];
      if (d.suppressed) flags.push('suppressed (test mute)');
      else if (d.skipped) flags.push('skipped — not sent');
      if (d.trigger) flags.push(d.trigger);
      return { text: `${d.subject || '(no subject)'} → ${to || '(no recipients)'}`, flags, warn: !!d.skipped };
    }
    case 'reminder_sent': {
      // Two jobs write this action: the scheduled reminder and the daily
      // deadline countdown (rows before the split carry no job = scheduled).
      const label = d.job === 'deadlineCountdown' ? 'Deadline countdown' : 'Scheduled reminder';
      if (d.skipped) {
        return { text: `${label} skipped — ${d.reason || 'nothing to send'}`, flags: [d.trigger].filter(Boolean), warn: true };
      }
      // Only the categories that run covered (older rows have none = all).
      const cats = Array.isArray(d.categories) && d.categories.length
        ? d.categories : ['lateAccounts', 'lateArchival', 'fsSigning'];
      const parts = [
        `sent ${d.sent ?? 0}/${d.recipientCount ?? 0} email(s)`,
        ...(cats.includes('lateAccounts') ? [`${d.lateAccountsCount ?? 0} late account(s)`] : []),
        ...(cats.includes('lateArchival') ? [`${d.lateArchivalCount ?? 0} archival reminder(s)`] : []),
        ...(cats.includes('fsSigning') ? [`${d.fsSigningCount ?? 0} expected FS submission(s)`] : []),
      ];
      const unmatched = Array.isArray(d.unmatched) ? d.unmatched.length : 0;
      const flags = [d.trigger].filter(Boolean);
      if (unmatched) flags.push(`${unmatched} lead(s) could not be emailed`);
      if (Array.isArray(d.failed) && d.failed.length) flags.push(`${d.failed.length} failed`);
      return { text: `${label}: ${parts.join(' · ')}`, flags, warn: unmatched > 0 || (d.failed || []).length > 0 };
    }
    case 'data_created':
    case 'data_updated':
    case 'data_deleted': {
      const verb = row.action === 'data_created' ? 'Created' : row.action === 'data_updated' ? 'Updated' : 'Deleted';
      const target = `${d.table || '?'}${d.id !== undefined && d.id !== null ? ` #${d.id}` : ''}`;
      const flags = [d.trigger].filter(Boolean);
      let text = `${verb} ${target}`;
      if (row.action === 'data_updated') {
        const keys = changedKeys(d.before, d.after);
        if (keys.length) text += ` — ${keys.slice(0, 6).join(', ')}${keys.length > 6 ? ` +${keys.length - 6} more` : ''}`;
      }
      return { text, flags, warn: false };
    }
    default: {
      const bits = [];
      if (d.reason) bits.push(d.reason);
      if (d.status) bits.push(d.status);
      if (d.endpoint) bits.push(`${d.method || ''} ${d.endpoint}`.trim());
      if (d.tokenType) bits.push(d.tokenType);
      if (d.activity) bits.push(d.activity);
      const text = bits.length ? bits.join(' · ') : JSON.stringify(d).slice(0, 140);
      return { text, flags: [], warn: /failed|locked|suspicious/.test(row.action) };
    }
  }
};

const SettingsActivityLog = () => {
  const [category, setCategory] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [email, setEmail] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEmail(email.trim()), 300);
    return () => clearTimeout(t);
  }, [email]);

  // Any filter change restarts from page 1.
  useEffect(() => { setPage(1); }, [category, action, debouncedQ, debouncedEmail, from, to]);
  // The action filter belongs to a category; switching category clears it.
  useEffect(() => { setAction(''); }, [category]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON('/api/admin/activity-log/actions');
        if (!cancelled) setActions(data.actions || []);
      } catch { /* the action dropdown is a convenience — the table still loads */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (action) params.set('action', action);
      if (debouncedQ) params.set('q', debouncedQ);
      if (debouncedEmail) params.set('email', debouncedEmail);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`/api/admin/activity-log?${params.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setOpen(new Set());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [category, action, debouncedQ, debouncedEmail, from, to, page]);

  useEffect(() => { load(); }, [load]);

  const actionOptions = useMemo(
    () => actions.filter((a) => !category || a.category === category),
    [actions, category]
  );
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">What the system did and who did it: every email sent or skipped, every weekly reminder run, every settings and workflow data change, and sign-in events. Times are Singapore time.</p>

      <div className="border-b border-slate-200 mb-4 flex flex-wrap gap-1">
        {CATEGORY_TABS.map(({ id, name, icon: Icon }) => (
          <button
            key={id || 'all'}
            type="button"
            onClick={() => setCategory(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              category === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {name}
          </button>
        ))}
      </div>

      {/* Toolbar — same shape as the other /manage list pages: wrapping row,
          search stretches, the rest keep their natural width. */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, recipient, table, request no…"
              className={`${inputCls} pl-9`}
            />
          </div>
          {/* inputCls is w-full — the wrapper sets each control's width. */}
          <div className="w-56">
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Who (email)"
              className={inputCls}
            />
          </div>
          <div className="w-64">
            <select value={action} onChange={(e) => setAction(e.target.value)} className={inputCls}>
              <option value="">Any action</option>
              {actionOptions.map((a) => (
                <option key={a.action} value={a.action}>{a.action} ({a.count})</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500 whitespace-nowrap">
            <div className="w-40">
              <input type="date" value={from} max={to || todayYmd()} onChange={(e) => setFrom(e.target.value)} className={inputCls} aria-label="From" />
            </div>
            <span>–</span>
            <div className="w-40">
              <input type="date" value={to} min={from || undefined} max={todayYmd()} onChange={(e) => setTo(e.target.value)} className={inputCls} aria-label="To" />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {(q || email || action || from || to) && (
              <button
                type="button"
                onClick={() => { setQ(''); setEmail(''); setAction(''); setFrom(''); setTo(''); }}
                className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                Clear Filters
              </button>
            )}
            <ResultCount count={total} noun="event" loading={loading} />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4 flex items-center gap-2">
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className={`${THEAD_CLS} text-left`}>When</th>
                <th className={`${THEAD_CLS} text-left`}>Type</th>
                <th className={`${THEAD_CLS} text-left`}>Action</th>
                <th className={`${THEAD_CLS} text-left`}>Who</th>
                <th className={`${THEAD_CLS} text-left w-full`}>What</th>
                <th className={THEAD_CLS} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading && rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No events match these filters.</td></tr>
              ) : rows.map((r) => {
                const s = summarise(r);
                const isOpen = open.has(r.id);
                return (
                  <React.Fragment key={r.id}>
                    <tr className={`hover:bg-slate-50 ${isOpen ? 'bg-slate-50' : ''}`}>
                      <td className="px-4 py-2 text-slate-600 whitespace-nowrap align-top">{fmtWhen(r.createdAt)}</td>
                      <td className="px-4 py-2 align-top whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_BADGE[r.category] || CATEGORY_BADGE.other}`}>
                          {(() => { const t = CATEGORY_TABS.find((c) => c.id === r.category); return t ? (t.badge || t.name) : r.category; })()}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-700 align-top whitespace-nowrap">{r.action}</td>
                      <td className="px-4 py-2 text-slate-700 align-top whitespace-nowrap">
                        {r.actorName || r.actorEmail ? (
                          <span title={r.actorEmail || ''}>{r.actorName || r.actorEmail}</span>
                        ) : (
                          <span className="text-slate-400">system</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-slate-800 align-top">
                        <div className={s.warn ? 'text-amber-800' : ''}>{s.text}</div>
                        {s.flags.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {s.flags.map((f, i) => (
                              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">{f}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 align-top text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => toggle(r.id)}
                          title={isOpen ? 'Hide details' : 'Show details'}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50">
                        <td colSpan={6} className="px-4 pb-3">
                          <div className="text-xs text-slate-500 mb-1">
                            {r.actorEmail && <span className="mr-3">Email: {r.actorEmail}</span>}
                            {r.ip && <span>IP: {r.ip}</span>}
                          </div>
                          <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 overflow-x-auto max-h-96 whitespace-pre-wrap break-words">{JSON.stringify(r.details, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
          <span className="text-slate-500">
            {total > 0 ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total.toLocaleString()}` : '0 of 0'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsActivityLog;
